import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { ClipboardList, CheckCircle, Clock, TrendingUp, MapPin, Calendar, Loader2 } from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTERNAL_STATUS_COLORS = {
  new_booking: '#94A3B8', confirmed: '#3B82F6', driver_assigned: '#6366F1',
  on_site: '#F59E0B', collected: '#22C55E', completed: '#16A34A',
  on_hold: '#EF4444', cancelled: '#6B7280',
};

const DriverDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const token = localStorage.getItem('driver_token');
        const result = await safeFetch(`${API_URL}/api/go/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (result.ok) setData(result.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#10B981]" /></div>;

  const stats = [
    { label: "Today's Jobs", value: data?.today_jobs || 0, icon: Clock, color: '#F59E0B', bg: '#FEF3C7' },
    { label: 'Active Jobs', value: data?.active_jobs || 0, icon: ClipboardList, color: '#3B82F6', bg: '#DBEAFE' },
    { label: 'Completed', value: data?.completed_jobs || 0, icon: CheckCircle, color: '#10B981', bg: '#D1FAE5' },
    { label: 'Total Jobs', value: data?.total_jobs || 0, icon: TrendingUp, color: '#8B5CF6', bg: '#EDE9FE' },
  ];

  return (
    <div className="space-y-6" data-testid="driver-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-[#64748B] text-sm mt-1">Welcome back! Here's your overview.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <Card key={s.label} className="border-0 bg-[#1E293B]" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.bg + '20' }}>
                <s.icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-xs text-[#64748B]">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upcoming Jobs */}
      <Card className="border-0 bg-[#1E293B]">
        <CardContent className="p-5">
          <h2 className="text-lg font-semibold text-white mb-4">Upcoming Jobs</h2>
          {(!data?.upcoming || data.upcoming.length === 0) ? (
            <p className="text-[#64748B] text-sm py-8 text-center">No upcoming jobs assigned</p>
          ) : (
            <div className="space-y-3">
              {data.upcoming.map(job => (
                <div key={job.id} className="flex items-start gap-3 p-3 rounded-lg bg-[#0F172A] border border-[#334155]">
                  <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: INTERNAL_STATUS_COLORS[job.internal_status] || '#94A3B8' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-white">{job.booking_ref}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{
                        backgroundColor: (INTERNAL_STATUS_COLORS[job.internal_status] || '#94A3B8') + '20',
                        color: INTERNAL_STATUS_COLORS[job.internal_status] || '#94A3B8'
                      }}>
                        {(job.internal_status || '').replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#64748B]">
                      <span className="flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        {job.waste_details?.site_address || job.delivery_details?.delivery_address || 'No address'}
                      </span>
                    </div>
                    {job.scheduled_date && (
                      <p className="text-xs text-[#94A3B8] mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {job.scheduled_date} {job.scheduled_time_window || ''}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverDashboard;
